<template>
  <div class="perm-table-wrap elevation-1">
    <table class="perm-table">
      <thead>
        <tr>
          <th class="perm-th-feature">
            <b-button class="btn btn-sm btn-light" @click.stop="$emit('toggle-expand-all')">
              <i :class="allCollapsed ? 'fas fa-expand-alt mr-1' : 'fas fa-compress-alt mr-1'" />
              {{ allCollapsed ? 'Mở rộng' : 'Thu gọn' }}
            </b-button>
          </th>
          <th
            v-for="role in visibleHeaders"
            :key="role.id"
            class="perm-th-role text-center"
            :style="{ '--role-color': roleColorMap.get(role.id) }"
          >
            <div class="perm-th-inner">
              <span class="perm-th-name">{{ getDisplayRoleName(role.text) }}</span>
              <b-dropdown
                v-if="isAssignPermission && role.code !== 'Admin'"
                size="sm"
                variant="link"
                no-caret
                right
                boundary="viewport"
              >
                <template #button-content>
                  <span class="perm-role-menu-btn">
                    <i class="fas fa-ellipsis-h" />
                  </span>
                </template>
                <b-dropdown-item @click="$emit('toggle-select-all', role)">
                  <i :class="isAllSelected(role) ? 'fas fa-times mr-2 text-warning' : 'fas fa-check-double mr-2 text-primary'" />
                  {{ isAllSelected(role) ? 'Bỏ chọn tất cả' : 'Chọn tất cả' }}
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item @click="$emit('edit-role', role)">
                  <i class="fas fa-pen mr-2 text-primary" />Chỉnh sửa
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </th>
        </tr>
      </thead>
      <template v-for="menuGroup in groupedPermissions">
        <!-- MenuGroup header (level 1) -->
        <tbody :key="'mg-' + menuGroup.menuGroup">
          <tr class="perm-menu-group-row" @click="$emit('toggle-group', menuGroup.menuGroup)">
            <td :colspan="visibleHeaders.length + 1">
              <div class="d-flex align-items-center">
                <v-icon small class="mr-3">mdi-{{ closedGroups.includes(menuGroup.menuGroup) ? 'chevron-up' : 'chevron-down' }}</v-icon>
                <span class="menu-group-name">{{ menuGroup.menuGroup }}</span>
                <span class="menu-group-count ml-2">({{ menuGroup.featureCount }} )</span>
              </div>
            </td>
          </tr>
        </tbody>
        <!-- SubGroups (level 2) -->
        <template v-if="!closedGroups.includes(menuGroup.menuGroup)">
          <PermissionGroup
            v-for="subGroup in menuGroup.subGroups"
            :key="'sg-' + menuGroup.menuGroup + '-' + subGroup.groupName"
            :group="subGroup"
            :parent-menu-group="menuGroup.menuGroup"
            :is-collapsed="closedGroups.includes(menuGroup.menuGroup + '|' + subGroup.groupName)"
            :col-span="visibleHeaders.length + 1"
            :visible-role-ids="visibleRoleIds"
            :role-color-map="roleColorMap"
            @toggle="$emit('toggle-group', menuGroup.menuGroup + '|' + $event)"
            @role-change="$emit('role-change', $event)"
          />
        </template>
      </template>
    </table>
  </div>
</template>

<script>
import PermissionGroup from './PermissionGroup.vue';
import { PERMISSION_TYPE } from '@/core/plugins/constants';

export default {
  name: 'PermissionTable',
  components: { PermissionGroup },
  props: {
    allHeaders: { type: Array, required: true },
    groupedPermissions: { type: Array, required: true },
    closedGroups: { type: Array, required: true },
    visibleRoleIds: { type: Set, required: true },
    isAssignPermission: { type: Boolean, default: false },
    permissions: { type: Array, required: true },
  },
  computed: {
    visibleHeaders() {
      return this.allHeaders.filter(h => h.id && this.visibleRoleIds.has(h.id));
    },
    allCollapsed() {
      // Check if all menuGroups are collapsed
      return this.groupedPermissions.every((mg) => this.closedGroups.includes(mg.menuGroup));
    },
    roleColorMap() {
      // Màu cố định cho từng role type
      const roleColors = {
        Staff: '#3699ff',    // Xanh dương
        Support: '#0bb783',  // Xanh lá
        Leader: '#8950fc',   // Tím
        Manager: '#f64e60',  // Đỏ
        Admin: '#ffa800',    // Cam
      };
      const getShortName = (name) => {
        if (!name) return name;
        const idx = name.indexOf('_');
        return idx > 0 ? name.substring(0, idx) : name;
      };
      const map = new Map();
      this.allHeaders.forEach(h => {
        if (!h.id) return;
        const shortName = getShortName(h.text);
        map.set(h.id, roleColors[shortName] || '#7e8299');
      });
      return map;
    },
  },
  methods: {
    getDisplayRoleName(fullName) {
      // Leader_Technology -> Leader
      if (!fullName) return fullName;
      const idx = fullName.indexOf('_');
      return idx > 0 ? fullName.substring(0, idx) : fullName;
    },
    isAllSelected(header) {
      return this.permissions.every((permission) =>
        permission.roles.every((role) => {
          if (role.roleId !== header.id) return true;
          const shortName = this.getDisplayRoleName(role.roleName);
          const isDisabled = shortName === 'Admin'
            ? permission.nameType !== PERMISSION_TYPE.ASSIGN
            : (shortName !== 'Manager' && permission.nameType === PERMISSION_TYPE.ASSIGN);
          if (isDisabled) return true;
          return role.checked;
        })
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.perm-table-wrap {
  overflow-x: auto;
  border-radius: 0;
}

.perm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead tr {
    background: #f8f9fc;
    border-bottom: 2px solid #e4e6ef;

    th {
      position: sticky;
      top: 0;
      z-index: 10;
      background: #f8f9fc;
    }
  }

  .perm-th-feature {
    text-align: left;
    padding: 10px 16px;
    font-weight: 600;
    color: #3f4254;
    min-width: 220px;
    white-space: nowrap;
  }

  .perm-th-role {
    padding: 8px 4px;
    font-weight: 600;
    color: #3f4254;
    min-width: 100px;
  }

  .perm-th-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-size: 12px;
  }

  .perm-th-name {
    line-height: 1.3;
    word-break: break-word;
    text-align: center;
  }

  .perm-role-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    color: #b5b5c3;
    font-size: 11px;

    &:hover {
      background: #e4e6ef;
      color: #3f4254;
    }
  }
}

// Menu Group (level 1)
.perm-menu-group-row {
  background: #e8f4f8;
  cursor: pointer;
  user-select: none;

  td {
    padding: 10px 12px;
    font-size: 14px;
    border-bottom: 2px solid #d0e8ef;
  }

  &:hover td {
    background: #dceef4;
  }
}

.menu-group-name {
  font-weight: 700;
  color: #1a5276;
  font-size: 14px;
}

.menu-group-count {
  font-size: 12px;
  color: #5dade2;
  font-weight: 500;
}
</style>
